import React, { useCallback, useMemo } from 'react';

import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { convertMarkdownToProsemirror } from '@atlassian/ai-model-io/convert-markdown-to-prosemirror';
import { useSubscribe } from '@atlassian/conversation-assistant-pubsub';

import {
	beforeDispatchAIRovoAgentTransaction,
	insertAIContentAtCurrentPosition,
	replaceWithAIContent,
} from '../../../prebuilt/config-item-actions/action-utils';

type SubscribeParams = Parameters<typeof useSubscribe>;

export const SubscribeToRovo = ({ editorView }: { editorView: EditorView }) => {
	// Used for testing
	// const publish = usePublish('ai-mate');

	const topic = useMemo<SubscribeParams[0]>(
		() => ({
			topic: 'ai-mate',
		}),
		[],
	);

	const callback = useCallback<SubscribeParams[1]>(
		(payload) => {
			if (payload.type === 'editor-suggestion') {
				const { state } = editorView;

				switch (payload.data.mode) {
					case 'insert': {
						const { pmFragment } = convertMarkdownToProsemirror({
							markdown: payload.data.content,
							schema: state.schema,
							idMap: {},
						});

						const tr = insertAIContentAtCurrentPosition({
							editorView,
							positions: [state.selection.from, state.selection.to],
							pmFragment,
						});
						beforeDispatchAIRovoAgentTransaction(tr);
						editorView.dispatch(tr);
						editorView.focus();
						break;
					}
					case 'replace': {
						const { pmFragment } = convertMarkdownToProsemirror({
							markdown: payload.data.content,
							schema: state.schema,
							idMap: {},
						});

						const tr = replaceWithAIContent({
							editorView,
							positions: [state.selection.from, state.selection.to],
							pmFragment,
							options: { replaceDocOnEmptySelection: false },
						});
						beforeDispatchAIRovoAgentTransaction(tr);
						editorView.dispatch(tr);
						editorView.focus();
						break;
					}
				}
			}
		},
		[editorView],
	);

	useSubscribe(topic, callback);

	return <></>;

	// Used for testing
	// return (
	// 	<div>
	// 		{/* eslint-disable-next-line @atlaskit/design-system/no-html-button, react/button-has-type */}
	// 		<button
	// 			onClick={() =>
	// 				publish({
	// 					type: 'editor-suggestion',
	// 					source: 'ai-mate',
	// 					data: {
	// 						mode: 'REPLACE',
	// 						content: 'some fake content for replacement!',
	// 					},
	// 				})
	// 			}
	// 		>
	// 			REPLACE TEST
	// 		</button>
	// 		{/* eslint-disable-next-line @atlaskit/design-system/no-html-button, react/button-has-type */}
	// 		<button
	// 			onClick={() =>
	// 				publish({
	// 					type: 'editor-suggestion',
	// 					source: 'ai-mate',
	// 					data: {
	// 						mode: 'INSERT',
	// 						content: 'some fake content for inserting!',
	// 					},
	// 				})
	// 			}
	// 		>
	// 			INSERT TEST
	// 		</button>
	// 	</div>
	// );
};
